<template>
	<div>
		<!-- filter -->
		<v-form ref="form">
			<v-row class="align-center mb-0">
				<v-col lg="3" md="4" sm="6" cols="12" class="pe-sm-0">
					<v-autocomplete
						v-model="vehicleId"
						:label="$t('inputs.vehicle-number-driver-assistant')"
						:items="vehicles"
						:item-text="item => `${item.BusNo} - ${item.DriverName} - ${item.AssistantName}`"
						item-value="Id"
						dense
						outlined
						clearable
						hide-details
						:loading="loading"
						:rules="rules.requiredAsNumber"
						@change="fetchPassengersAndRoutPoints"
					></v-autocomplete>
				</v-col>
				<v-col cols="auto" class="pe-sm-0">
					<v-btn
						color="green white--text"
						:disabled="!vehicleId || loading"
						:loading="submitLoading"
						type="submit"
						@click="savePath"
					>
						{{$t('save')}}
					</v-btn>
				</v-col>
			</v-row>
		</v-form>

		<!-- map -->
		<div :style="{ height: `${$vuetify.breakpoint.height - 150}px` }">
			<v-map
				ref="map"
				:points="companyCoordinates ? [companyCoordinates, ...points] : points"
				:polyline-points.sync="polylinePoints"
				:context-menu-items="!isRoutingActive ? contextMenuItems : []"
				:loading="loading"
				@context-menu-add="addMarker"
				@context-menu-remove="removeMarker"
			>
				<template v-slot:bottom-left-area>
					<!-- <v-btn
						v-if="isRoutingActive"
						small
						height="45"
						width="45"
						min-width="45"
						depressed
						color="green lighten-1 white--text"
						class="me-2"
						@click="polylinePoints = routingWaypoints; deactivateRouting(true)"
					>
						<v-icon>mdi-check</v-icon>
					</v-btn> -->
					<div class="d-flex flex-column">
						<!-- path tools -->
						<v-speed-dial
							v-if="polylinePoints.length || oldPolylinePoints.length"
							v-model="pathTools"
							direction="top"
							transition="scale-transition"
						>
							<template v-slot:activator>
								<v-btn
									v-model="pathTools"
									color="primary lighten-1"
									dark
									small
									height="45"
									width="45"
									min-width="45"
									depressed
									class="mb-2"
								>
									<v-icon>
										mdi-{{ pathTools ? 'close' : 'vector-polyline-edit' }}
									</v-icon>
								</v-btn>
							</template>

							<!-- delete path -->
							<v-tooltip right>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										v-if="polylinePoints.length"
										fab
										dark
										small
										color="error lighten-1"
										v-bind="attrs"
										v-on="on"
										@click="
											oldPolylinePoints = polylinePoints;
											polylinePoints = [];
										"
									>
										<v-icon>mdi-vector-polyline-remove</v-icon>
									</v-btn>
								</template>
								{{$t('MapManagement.delete-path')}}
							</v-tooltip>
							
							<!-- restore path -->
							<v-tooltip right>
								<template v-slot:activator="{ on, attrs }">
									<v-btn
										v-if="oldPolylinePoints.length"
										fab
										dark
										small
										color="orange lighten-1"
										v-bind="attrs"
										v-on="on"
										@click="
											polylinePoints = oldPolylinePoints;
											oldPolylinePoints = [];
										"
									>
										<v-icon>mdi-backup-restore</v-icon>
									</v-btn>
								</template>
								{{$t('MapManagement.restore-path')}}
							</v-tooltip>
						</v-speed-dial>

						<!-- routing button -->
						<v-btn
							small
							height="45"
							width="45"
							min-width="45"
							depressed
							:color="!isRoutingActive ? 'green lighten-1' : 'purple lighten-2'"
							:disabled="!isRoutingActive && polylinePoints.length < 2"
							@click="!isRoutingActive ? activateRouting() : deactivateRouting()"
						>
							<v-img
								:src="
									isRoutingActive
										? require('@/assets/imgs/routing.png')
										: require('@/assets/imgs/path.png')
								"
							/>
							<v-icon v-if="0">
								mdi-chart-timeline-variant
							</v-icon>
								<!-- mdi-map-marker-distance -->
						</v-btn>
					</div>
				</template>
			</v-map>
		</div>
	</div>
</template>

<script>
import rules from '@/helpers/validation rules';
import { mapState } from 'vuex';
export default {
	name: 'MapManagement',

	data: () => ({
		loading: false,
		submitLoading: false,
		pathTools: false,

		rules,

		isRoutingActive: false,
		routingWaypoints: [],
		oldPolylinePoints: [],

		// data
		vehicleId: null,
		points: [],
		polylinePoints: [],
	}),

	watch: {
		polylinePoints(newVal) {
			if (newVal.length === 0) {
				this.routingWaypoints = this.$refs.map.getWaypoints();
			}
		}
	},

	computed: {
		...mapState({
			vehicles: state => state.vehicles.vehicles,
			companyInfo: state => state.companyInfo.companyInfo,
		}),

		contextMenuItems() {
			return [
				{
					text: this.$t('MapManagement.contextmenu.add-point'),
					action: 'add',
					icon: 'mdi-map-marker-plus',
					iconColor: 'success darken-2',
					class: 'success--text text--darken-2'
				},
				{
					text: this.$t('MapManagement.contextmenu.remove-point'),
					icon: 'mdi-map-marker-remove',
					action: 'remove',
					iconColor: 'error darken-1',
					class: 'error--text text--darken-1'
				},
			]
		},

		companyCoordinates() {
			if (this.companyInfo.SchoolLocation.Lat && this.companyInfo.SchoolLocation.Lng) {
				return {
					latLng: {
						lat: this.companyInfo.SchoolLocation.Lat,
						lng: this.companyInfo.SchoolLocation.Lng
					},
					icon: {
						size: [44, 44],
						anchor: [22, 44],
						url: require('@/assets/imgs/star.png')
					},
					popupText: this.$t('company-location'),
					draggable: false
				}
			}
			return null
		},
	},

	methods: {
		fetchPassengersAndRoutPoints(val) {
			if (!val) {
				this.points = [];
				this.polylinePoints = [];
				this.oldPolylinePoints = [];
				return
			}

			this.loading = true;
			Promise.all([
				this.$store.dispatch('vehicles/fetchPath', { id: this.vehicleId })
					.then((data) => {
						this.polylinePoints = data.map(c => ({ lat: c.Lat, lng: c.Lng }));
					}),
				this.$store.dispatch('passengers/fetchByVehicleId', { vehicleId: this.vehicleId })
					.then((data) => {
						this.points = [];
						for (const item of data) {
							if (item.Lat && item.Lng) {
								this.points.push({
									latLng: { lat: item.Lat, lng: item.Lng },
									icon: {
										size: [44, 44],
										anchor: [22, 44],
										url: require('@/assets/imgs/home.png')
									},
									popupText: `${item.FirstName} ${item.FatherName || ''} ${item.LastName}`,
									draggable: false
								})
							}
						}
					})
			]).finally(() => {
				this.loading = false;
			})
		},

		savePath() {
			if (this.isRoutingActive) {
				this.deactivateRouting();
			}
			if (this.polylinePoints.length < 2) {
				this.$eventBus.$emit('show-snackbar', 'warning', this.$t('messages.set-location'));
				return
			}
			this.submitLoading = true;
			this.$store.dispatch('vehicles/updatePath', {
				id: this.vehicleId,
				points: this.polylinePoints
			}).then(() => {
				this.$eventBus.$emit('show-snackbar', 'info', this.$t('messages.saved-successfully'));
			}).finally(() => {
				this.submitLoading = false;
			})
		},

		// map
		addMarker(coordinates) {
			if (this.$refs.form.validate()) {
				this.polylinePoints.push(coordinates);
			} else {
				this.$eventBus.$emit('show-snackbar', 'warning', this.$t('MapManagement.select-vehicle'));
			}
		},
		removeMarker(coordinates) {
			const indexMarker = this.polylinePoints.findIndex((c) =>
				c.lat === coordinates.lat && c.lng === coordinates.lng
			)
			this.polylinePoints.splice(indexMarker, 1);
		},

		// map routing
		activateRouting() {
			this.isRoutingActive = true;
			this.$refs.map.activateRouting(this.polylinePoints);
		},
		deactivateRouting(isSave = false) {
			this.isRoutingActive = false;
			this.routingWaypoints = [];
			this.$refs.map.removeRouting(this.polylinePoints);
			if (!isSave) {
				this.polylinePoints = this.$refs.map.getWaypoints(true);
			}
		}
	},

	mounted() {
		if (this.companyCoordinates) {
			this.$refs.map.setViewTo(this.companyCoordinates.latLng);
		}
		
		const init = () => {
			if (this.$route.query.vehicleId) {
				this.vehicleId = Number(this.$route.query.vehicleId);
				this.fetchPassengersAndRoutPoints(true);
			}
		}

		if (!this.vehicles.length) {
			this.loading = true;
			this.$store.dispatch('vehicles/fetchAll').finally(() => {
				this.loading = false;
				init();
			})
		} else {
			init();
		}
	}
}
</script>

<style lang="scss" scoped>
.disabled-image {
	filter: brightness(100%)
}
</style>